import { Injectable } from '@angular/core';
import type { Observable } from 'rxjs';
import { Subject, merge } from 'rxjs';
import type {
  PaymentsError,
  PaymentsEvent,
  PaymentsMessage,
  PaymentsOverlay,
  PaymentsResult,
} from './payments-messaging.types';
import {
  isPaymentsError,
  isPaymentsEvent,
  isPaymentsOverlay,
  isPaymentsResult,
} from './payments-messaging.types';

@Injectable({
  providedIn: 'root',
})
export class PaymentsMessagingService {
  private paymentsEventsSubject$ = new Subject<PaymentsEvent>();
  private paymentsResultsSubject$ = new Subject<PaymentsResult>();
  private paymentsOverlaysSubject$ = new Subject<PaymentsOverlay>();
  private paymentsErrorsSubject$ = new Subject<PaymentsError>();

  private paymentsEvents$ = this.paymentsEventsSubject$.asObservable();
  private paymentsResults$ = this.paymentsResultsSubject$.asObservable();
  private paymentsOverlays$ = this.paymentsOverlaysSubject$.asObservable();
  private paymentsErrors$ = this.paymentsErrorsSubject$.asObservable();

  private paymentsMessages$ = merge(
    this.paymentsEvents$,
    this.paymentsResults$,
    this.paymentsOverlays$,
    this.paymentsErrors$,
  );

  getErrorStream(): Observable<PaymentsError> {
    return this.paymentsErrors$;
  }

  pushError(paymentsError: PaymentsError): void {
    this.paymentsErrorsSubject$.next(paymentsError);
  }

  getEventStream(): Observable<PaymentsEvent> {
    return this.paymentsEvents$;
  }

  pushEvent(paymentsEvent: PaymentsEvent): void {
    this.paymentsEventsSubject$.next(paymentsEvent);
  }

  getMessageStream(): Observable<
    PaymentsEvent | PaymentsResult | PaymentsOverlay | PaymentsError
  > {
    return this.paymentsMessages$;
  }

  pushMessage(paymentsMessage: PaymentsMessage): void {
    if (isPaymentsError(paymentsMessage)) {
      this.pushError(paymentsMessage);
    } else if (isPaymentsEvent(paymentsMessage)) {
      this.pushEvent(paymentsMessage);
    } else if (isPaymentsOverlay(paymentsMessage)) {
      this.pushOverlay(paymentsMessage);
    } else if (isPaymentsResult(paymentsMessage)) {
      this.pushResult(paymentsMessage);
    }
    // fail silently
  }

  getOverlayStream(): Observable<PaymentsOverlay> {
    return this.paymentsOverlays$;
  }

  pushOverlay(paymentsOverlay: PaymentsOverlay): void {
    this.paymentsOverlaysSubject$.next(paymentsOverlay);
  }

  getResultStream(): Observable<PaymentsResult> {
    return this.paymentsResults$;
  }

  pushResult(paymentsResult: PaymentsResult): void {
    this.paymentsResultsSubject$.next(paymentsResult);
  }
}
